import { ChevronLeft, ChevronRight, ChevronDown, Check, CalendarX2 } from "lucide-react";
import { Link } from "react-router-dom";

import { normalizePlans, normalizeSaved } from "@/lib/coreShapes";
import { planItems, plansFromActivities, normalizeTrusted, monthWeeks, monthMarks, monthTitle, shiftMonth, dayLabel, WEEKDAYS } from "@/lib/v3Plans";
import { useCore } from "../useCore";
import { Unavailable } from "./kit";
import { StatusBanner, V3Actions, V3_PRIMARY } from "./v3";
import { V3_CARD } from "./v3app";

// V3 building blocks for plans, saved items and safety (M21–M24, M27, M93–M97).
export const enc = encodeURIComponent;
export const Blocked = ({ id, children }) => <div className="mt-[12px]" id={id}><Unavailable testid={id}>{children}</Unavailable></div>;

// GET /member/plans (Core 1.1) + GET /member/saved-activities; an older Core without plans falls back to own activity participations.
export function usePlanItems() {
  const plans = useCore("/member/plans", (d) => ({ list: normalizePlans(d) }));
  const odd = plans.error === "notfound" || (plans.data && plans.data.list === null);
  const fallback = useCore(odd ? "/member/activities?category=all" : null, (d) => ({ list: plansFromActivities(d) }));
  const saved = useCore("/member/saved-activities", (d) => ({ list: normalizeSaved(d) }));
  const own = odd ? fallback : plans;
  const reload = own.reload;
  const error = own.error || (own.data && own.data.list === null);
  const savedDone = saved.error || saved.data;
  const items = !error && own.data?.list && savedDone ? planItems(own.data.list, saved.data?.list || []) : null;
  return { items, error: Boolean(error), reload, note: odd, savedMissing: Boolean(saved.error || (saved.data && saved.data.list === null)) };
}

export const useTrusted = () => useCore("/member/trusted-contacts", (d) => ({ list: normalizeTrusted(d) }));

const TONE = { confirmed: "bg-v3-mint font-bold text-v3-green", requested: "bg-v3-sand font-bold text-v3-amber", saved: "bg-v3-lilac font-bold text-v3-ink", cancelled: "bg-red-50 text-red-800 line-through", other: "bg-v3-lilac text-v3-muted" };
const LEGEND = [["bg-v3-magenta", "Ausgewählt"], ["bg-v3-mint", "Bestätigt"], ["bg-v3-sand", "Anfrage offen"], ["bg-v3-lilac", "Gemerkt"], ["bg-red-50 border border-red-200", "Abgesagt"]];
const CELL = "flex h-[31px] w-full items-center rounded-[6px] pl-[8px] text-[14px] leading-[19px]";

function Day({ d, info, month, selected, onSelect }) {
  if (!info) return <span className={`${CELL} text-v3-ink`}>{d}</span>;
  const on = info.items.some((i) => i.id === selected);
  return (
    <button type="button" onClick={() => onSelect(info.items)} aria-pressed={on} aria-label={dayLabel(d, month, info.items)} data-testid={`plans-day-${d}`} data-mark={info.mark}
      className={`${CELL} transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-v3-ink ${on ? "bg-v3-magenta font-bold text-white" : `${TONE[info.mark]} hover:ring-1 hover:ring-v3-magenta`}`}>{d}</button>
  );
}

const NavBtn = ({ label, onClick, icon: Icon, testid }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testid}
    className="flex h-[36px] w-[36px] items-center justify-center rounded-[8px] text-v3-muted transition-colors hover:bg-v3-lilac hover:text-v3-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-v3-magenta"><Icon className="h-[18px] w-[18px]" aria-hidden="true" /></button>
);

// M21/M93 month card: days with entries are buttons with their own label; colour is never the only hint.
export function MonthCalendar({ items, month, onMonth, selected, onSelect }) {
  const marks = monthMarks(items, month);
  return (
    <section className={`${V3_CARD} mb-[21px] min-h-[344px] pb-[18px] pl-[13px] pr-[17px] pt-[9px] lg:min-h-[410px]`} aria-labelledby="plans-month" data-testid="plans-calendar">
      <div className="flex items-center justify-between gap-2 pl-[7px]">
        <h2 id="plans-month" className="text-[22px] font-bold leading-[30px] text-v3-ink" data-testid="plans-month" aria-live="polite">{monthTitle(month)}</h2>
        <div className="flex gap-[2px]">
          <NavBtn label="Vorheriger Monat" icon={ChevronLeft} onClick={() => onMonth(shiftMonth(month, -1))} testid="plans-month-prev" />
          <NavBtn label="Nächster Monat" icon={ChevronRight} onClick={() => onMonth(shiftMonth(month, 1))} testid="plans-month-next" />
        </div>
      </div>
      <table className="mt-[8px] w-full table-fixed border-separate border-spacing-x-0 border-spacing-y-[6px] lg:mt-[3px] lg:border-spacing-y-[15px]" aria-labelledby="plans-month">
        <thead><tr>{WEEKDAYS.map(([s, l]) => <th key={s} scope="col" abbr={l} className="pb-[4px] pl-[4px] text-left text-[12px] font-bold leading-[18px] text-v3-muted lg:pb-0">{s}</th>)}</tr></thead>
        <tbody>{monthWeeks(month).map((w, i) => (
          <tr key={i}>{w.map((d, j) => <td key={j} className="p-0 pr-[4px]">{d ? <Day d={d} info={marks[d]} month={month} selected={selected} onSelect={onSelect} /> : null}</td>)}</tr>))}</tbody>
      </table>
      <ul className="mt-[4px] flex flex-wrap gap-x-[16px] gap-y-[6px] pl-[7px]" aria-label="Kennzeichnungen" data-testid="plans-legend">
        {LEGEND.map(([c, l]) => <li key={l} className="flex items-center gap-[6px] text-[12.5px] text-v3-muted"><span aria-hidden="true" className={`h-[12px] w-[12px] rounded-[3px] ${c}`} />{l}</li>)}
      </ul>
    </section>
  );
}

const ROW = "flex min-h-[90px] w-full items-start gap-[16px] rounded-[10px] border border-v3-line bg-white px-[18px] pb-[18px] pt-[11px] text-left";
const Box = ({ checked }) => (
  <span aria-hidden="true" className={`mt-[12px] flex h-[21px] w-[21px] shrink-0 items-center justify-center rounded-[4px] border transition-colors ${checked ? "border-v3-magenta bg-v3-magenta text-white" : "border-v3-line bg-white"}`}>
    {checked && <Check className="h-[14px] w-[14px]" strokeWidth={3} />}
  </span>
);
const Body = ({ title, value, testid }) => (
  <span className="min-w-0">
    <span className="block break-words text-[16px] font-bold leading-[22px] text-v3-ink">{title}</span>
    <span className="mt-[11px] block break-words text-[14px] leading-[20px] text-v3-muted" data-testid={testid && `${testid}-value`}>{value}</span>
  </span>
);

// M24/M94/M97 row. Without `input` the square only mirrors a state; with it, a real checkbox or radio sits in the label.
export function CheckRow({ title, value, checked, input, to, testid }) {
  if (input) return (
    <label className={`${ROW} cursor-pointer transition-colors hover:border-v3-magenta focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-v3-magenta`} data-testid={testid}>
      <input {...input} checked={checked} className="sr-only" data-testid={testid && `${testid}-input`} />
      <Box checked={checked} /><Body title={title} value={value} testid={testid} />
    </label>
  );
  if (to) return <Link to={to} className={`${ROW} transition-colors hover:border-v3-magenta`} data-testid={testid}><Box checked={checked} /><Body title={title} value={value} testid={testid} /></Link>;
  return <div className={ROW} data-testid={testid}><Box checked={checked} /><Body title={title} value={value} testid={testid} /></div>;
}
export const CheckList = ({ children, label }) => <div className="space-y-[13px]" role={label ? "group" : undefined} aria-label={label}>{children}</div>;

// Select styled as a detail row (label above, value below).
export function RowSelect({ id, label, value, onChange, children, error, testid }) {
  return (
    <div className={`min-h-[70px] rounded-[10px] border bg-white px-[16px] pb-[10px] pt-[10px] focus-within:border-v3-magenta lg:min-h-[73px] ${error ? "border-red-700" : "border-v3-line"}`}>
      <label htmlFor={id} className="block text-[13px] font-bold leading-[18px] text-v3-muted lg:text-[14px] lg:leading-[19px]">{label}</label>
      <div className="relative">
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} aria-invalid={error ? "true" : undefined} aria-describedby={error ? `${id}-error` : undefined}
          className="mt-[2px] min-h-[30px] w-full cursor-pointer appearance-none bg-transparent pr-[28px] text-[14.5px] text-v3-ink outline-none lg:text-[16px]">{children}</select>
        <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-v3-muted" aria-hidden="true" />
      </div>
      {error && <span id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-[4px] block text-[13px] font-bold text-red-800">{error}</span>}
    </div>
  );
}

export function NoPlan({ testid = "plans-none" }) {
  return (<>
    <StatusBanner tone="sand" icon={CalendarX2} title="Noch kein Termin" text="Dafür brauchst du eine Teilnahme oder Anfrage bei einer Aktivität." testid={testid} />
    <V3Actions><Link to="/aktivitaeten" data-testid={`${testid}-discover`} className={V3_PRIMARY}>Aktivitäten entdecken</Link></V3Actions>
  </>);
}

export const FieldError = ({ id, children }) => (children ? <p id={id} role="alert" data-testid={id} className="mt-[8px] text-[13px] font-bold text-red-800">{children}</p> : null);
